import { Device } from '@capacitor/device';
import { Capacitor } from '@capacitor/core';
import { isAndroidCapacitor } from '../utils/platform';
import { waitForCapacitor } from '../utils/capacitorReady';

// Enable logging only in development
const DEBUG = false;

const log = (message: string, ...args: any[]) => {
  if (DEBUG) {
    console.log(`[CapacitorLanguageDetector] ${message}`, ...args);
  }
};

/**
 * Custom language detector for Capacitor that uses the Device API
 * to get the actual device locale on Android
 */
// Store the detected language for synchronous access
let detectedLanguageCache: string | undefined = undefined;

// Initialize cache immediately for web platforms
if (typeof window !== 'undefined' && !Capacitor.isNativePlatform()) {
  const browserLang = navigator.language || navigator.languages?.[0];
  if (browserLang) {
    detectedLanguageCache = browserLang.split('-')[0].toLowerCase();
  }
}

export const capacitorLanguageDetector = {
  name: 'capacitorLanguageDetector',
  type: 'languageDetector' as const,
  async: true,
  lookup: () => {
    return detectedLanguageCache;
  },
  detect: async (callback: (lng: string | readonly string[] | undefined) => void) => {
    const startTime = Date.now();
    log('detect() called at:', new Date().toISOString());
    log('Capacitor.isNativePlatform():', Capacitor.isNativePlatform());
    log('Capacitor.getPlatform():', Capacitor.getPlatform());
    log('isAndroidCapacitor():', isAndroidCapacitor());
    log('window.Capacitor exists:', !!window.Capacitor);
    
    // For web platforms, detect language immediately without async operations
    if (!isAndroidCapacitor()) {
      const browserLang = navigator.language || navigator.languages?.[0];
      if (browserLang) {
        const languageCode = browserLang.split('-')[0].toLowerCase();
        detectedLanguageCache = languageCode;
        callback(languageCode);
        return;
      }
    }
    
    try {
      // Only use Device API on Android Capacitor
      if (isAndroidCapacitor()) {
        log('Android Capacitor detected, waiting for platform to be ready');
        
        // Ensure Capacitor is ready
        await waitForCapacitor();
        
        log('Platform ready, using Device API');
        
        // Log current localStorage value
        const storedLang = localStorage.getItem('i18nextLng');
        log('Current localStorage i18nextLng:', storedLang);
        
        // Try to get the full language tag first (e.g., 'en-US', 'lt-LT')
        try {
          const langTag = await Device.getLanguageTag();
          log('Device.getLanguageTag() result:', langTag);
          
          if (langTag?.value) {
            // Extract just the language code (e.g., 'en' from 'en-US')
            const languageCode = langTag.value.split('-')[0].toLowerCase();
            log('Extracted language code from tag:', languageCode);
            detectedLanguageCache = languageCode; // Cache the value
            log('Cached language:', detectedLanguageCache);
            log('Calling callback with:', languageCode);
            log(`Detection completed in ${Date.now() - startTime}ms`);
            callback(languageCode);
            return;
          }
        } catch (e) {
          // Fall back to getLanguageCode if getLanguageTag is not available
          log('getLanguageTag error:', e);
          console.warn('getLanguageTag not available, falling back to getLanguageCode', e);
        }
        
        // Fall back to language code (2-letter code)
        try {
          const langCode = await Device.getLanguageCode();
          log('Device.getLanguageCode() result:', langCode);
          
          if (langCode?.value) {
            const languageCode = langCode.value.toLowerCase();
            log('Language code (lowercase):', languageCode);
            detectedLanguageCache = languageCode; // Cache the value
            log('Cached language:', detectedLanguageCache);
            log('Calling callback with:', languageCode);
            log(`Detection completed in ${Date.now() - startTime}ms`);
            callback(languageCode);
            return;
          }
        } catch (e) {
          log('getLanguageCode error:', e);
        }
        
        // Log device info for debugging
        try {
          await Device.getInfo();
        } catch (e) {
          log('Error getting device info:', e);
        }
      } else {
        log('Not Android Capacitor, using browser detection');
        log('navigator.language:', navigator.language);
        log('navigator.languages:', navigator.languages);
        
        // For web platforms, immediately use browser detection
        const browserLang = navigator.language || navigator.languages?.[0];
        log('Browser language:', browserLang);
        
        if (browserLang) {
          // Extract just the language code
          const languageCode = browserLang.split('-')[0].toLowerCase();
          log('Extracted browser language code:', languageCode);
          detectedLanguageCache = languageCode; // Cache the value
          log('Cached language:', detectedLanguageCache);
          log('Calling callback with:', languageCode);
          log(`Detection completed in ${Date.now() - startTime}ms`);
          callback(languageCode);
          return; // Important: return here to avoid calling the callback twice
        } else {
          log('No browser language detected, calling callback with undefined');
          log(`Detection completed in ${Date.now() - startTime}ms`);
          callback(undefined);
          return;
        }
      }
      
      // This fallback should only be reached if Android detection fails
      log('Android detection failed, falling back to browser detection');
      const browserLang = navigator.language || navigator.languages?.[0];
      log('Browser language fallback:', browserLang);
      
      if (browserLang) {
        // Extract just the language code
        const languageCode = browserLang.split('-')[0].toLowerCase();
        log('Extracted browser language code:', languageCode);
        detectedLanguageCache = languageCode; // Cache the value
        log('Cached language:', detectedLanguageCache);
        log('Calling callback with:', languageCode);
        log(`Detection completed in ${Date.now() - startTime}ms`);
        callback(languageCode);
      } else {
        log('No language detected, calling callback with undefined');
        log(`Detection completed in ${Date.now() - startTime}ms`);
        callback(undefined);
      }
    } catch (error) {
      console.error('Error detecting language:', error);
      log('Fatal error in detect():', error);
      
      // Fall back to browser language on error
      const browserLang = navigator.language || navigator.languages?.[0];
      const fallbackLang = browserLang ? browserLang.split('-')[0].toLowerCase() : undefined;
      log('Error fallback language:', fallbackLang);
      callback(fallbackLang);
    }
  },
  
  // Optional: implement caching
  cacheUserLanguage: async (lng: string) => {
    log('cacheUserLanguage() called with:', lng);
    
    // We can still use localStorage for caching the detected language
    try {
      localStorage.setItem('i18nextLng', lng);
      log('Language cached successfully');
    } catch (e) {
      console.error('Error caching language:', e);
      log('Error caching language:', e);
    }
  }
};